import dayjs from 'dayjs';
import { Asset, AssetStatus } from '../generated/gql-types';
import type { DocumentDateFieldKey } from './documentSlots.util';

export function parseAssetMetadata(
  metadata: Asset['metadata'],
): Record<string, unknown> {
  if (!metadata) {
    return {};
  }
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return metadata as Record<string, unknown>;
}

export function getMetadataDateMs(
  metadata: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const key of keys) {
    const raw = metadata[key];
    if (raw == null || raw === '') {
      continue;
    }
    const ms = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(ms) || ms <= 0) {
      continue;
    }
    const d = dayjs(ms);
    if (d.isValid()) {
      return ms;
    }
  }
  return null;
}

export function formatMetadataDate(ms: number | null): string | null {
  if (ms == null) {
    return null;
  }
  return dayjs(ms).format('D MMM YYYY');
}

export function epochMsToDateInputValue(ms: number | null): string {
  if (ms == null) {
    return '';
  }
  return dayjs(ms).format('YYYY-MM-DD');
}

export type AssetDateDisplay = {
  issueDateLabel: string | null;
  expiryDateLabel: string | null;
  isExpired: boolean;
};

export function getAssetDateDisplay(asset: Asset): AssetDateDisplay {
  const meta = parseAssetMetadata(asset.metadata);
  const issueMs = getMetadataDateMs(meta, ['issueDate', 'issuedAt']);
  const expiryMs = getMetadataDateMs(meta, [
    'expiryDate',
    'expiresAt',
    'expirationDate',
  ]);

  const isExpired =
    asset.status === AssetStatus.Expired ||
    (expiryMs != null && dayjs(expiryMs).isBefore(dayjs(), 'day'));

  const issueFormatted = formatMetadataDate(issueMs);
  const expiryFormatted = formatMetadataDate(expiryMs);

  return {
    issueDateLabel: issueFormatted ? `Issued ${issueFormatted}` : null,
    expiryDateLabel: expiryFormatted
      ? isExpired
        ? `Expired ${expiryFormatted}`
        : `Expires ${expiryFormatted}`
      : null,
    isExpired: Boolean(expiryMs && isExpired),
  };
}

export function buildUploadMetadata(
  dates: Partial<Record<DocumentDateFieldKey, string>>,
): Record<string, string> {
  const metadata: Record<string, string> = {};

  if (dates.issueDate) {
    const issueMs = dayjs(dates.issueDate).startOf('day').valueOf();
    metadata.issueDate = String(issueMs);
  }

  if (dates.expiryDate) {
    const expiryMs = dayjs(dates.expiryDate).startOf('day').valueOf();
    metadata.expiryDate = String(expiryMs);
    metadata.expiresAt = String(expiryMs);
  }

  return metadata;
}

export function validateRequiredDocumentDates(
  dates: Partial<Record<DocumentDateFieldKey, string>>,
  requirements: Partial<
    Record<DocumentDateFieldKey, { required: boolean; title: string }>
  >,
): string | null {
  for (const key of ['issueDate', 'expiryDate'] as const) {
    const requirement = requirements[key];
    if (requirement?.required && !dates[key]?.trim()) {
      return `${requirement.title} is required`;
    }
  }
  return null;
}
